<template>
  <v-card
    v-if="currentStep === paymentDeliveryStep"
    class="donation-flow"
    :class="compact && 'elevation-0 pa-0'"
    style="padding: 40px; border-radius: 16px;"
  >
    <v-card-title
      class="text-center mx-auto d-flex justify-space-between mb-8"
      style="padding: 0; line-height: 1;"
    >
      <span class="donation-flow__step-title">
        {{ t('donationGuide.paymentDeliveryDonationDetails.title') }}
      </span>
      <span class="step-indicator">{{ t('donationGuide.progress.mobileText', { current: 2, total: 3 }) }}</span>
    </v-card-title>
    <v-card-text class="pa-0">
      <v-form
        ref="form"
        v-model="valid"
        class="mb-3"
        data-cy="paymentdelivery-view-form"
        lazy-validation
      >
        <div v-if="isMemoryGift">
          <delivery-method-select-component
            v-model="donationDetails.deliveryMethod"
            :delivery-methods="deliveryMethodsRef"
            :loading="deliveryMethodsRef.length === 0"
            @change="deliveryMethodChanged"
            density="comfortable"
            class="mb-8"
          />
          <v-slide-y-transition>
            <v-text-field
              v-if="deliveryIsEmail"
              v-model="donationDetails.recipient.name"
              :hint="t('donationDetails.form.recipient.hint')"
              :label="t('donationDetails.form.recipient.name') + '*'"
              :persistent-hint="true"
              :rules="[rules.requiredField(String(t('donationDetails.form.recipient.name')))]"
              :validate-on-blur="true"
              class="mb-3"
              data-cy="paymentdelivery-view-namefield"
              required
              density="comfortable"
            />
          </v-slide-y-transition>
          <v-slide-y-transition>
            <v-text-field
              v-if="deliveryIsEmail"
              v-model="donationDetails.recipient.email"
              :hint="t('donationDetails.form.deliveryMethod.emailHint')"
              :label="t('donationDetails.form.recipient.email') + '*'"
              :persistent-hint="true"
              :placeholder="t('donorDetails.form.email.placeholder')"
              :rules="[
                rules.email,
                rules.requiredField(String(t('donationDetails.form.recipient.email'))),
              ]"
              :validate-on-blur="true"
              class="mb-3"
              data-cy="paymentdelivery-view-emailfield"
              required
              type="email"
              @keydown.space.prevent
              density="comfortable"
            />
          </v-slide-y-transition>
        </div>
        <payment-method-select-component
          v-model="donationDetails.paymentSpecification.paymentMethod"
          :payment-methods="requestSession.paymentMethods"
          data-cy="paymentdelivery-view-select-component"
          @change="getAdminFee"
          density="comfortable"
          hide-divider
        />
        <v-slide-y-transition>
          <v-text-field
            v-if="isEmailInvoice"
            v-model="invoiceEmail"
            :persistent-hint="true"
            :validate-on-blur="true"
            :label="t('donorDetails.form.invoice.label') + '*'"
            :placeholder="t('donorDetails.form.invoice.placeholder')"
            :hint="t('donationDetails.form.paymentMethod.invoiceEmailHint')"
            :rules="[
              rules.email,
              rules.requiredField(String(t('donationDetails.form.recipient.email'))),
            ]"
            data-cy="paymentdelivery-view-invoice-email"
            required
            class="mb-3"
            density="comfortable"
          />
        </v-slide-y-transition>
        <v-slide-y-transition>
          <div v-if="isPhoneBased">
            <option-label :title="mobileLabel + '*'" />
            <v-text-field
              v-model="mobileNumber"
              :hint="t('donationDetails.form.mobileNumber.hint')"
              :placeholder="t('donorDetails.form.mobileNumber.label')"
              :persistent-hint="true"
              :rules="[rules.mobilePhoneNumber, rules.requiredField(mobileLabel)]"
              :validate-on-blur="true"
              persistent-placeholder
              clearable
              data-cy="paymentdelivery-view-mobilenumber"
              required
              @keydown.space.prevent
              density="comfortable"
            />
          </div>
        </v-slide-y-transition>
        <v-card class="mt-2 mb-5" flat>
          <option-label :title="t('donationDetails.form.donationAmount.label') + '*'" class="mb-2" />
          <donation-amount-component
            v-model="donationDetails.paymentSpecification.causeAmount"
            :admin-fee="donationDetails.paymentSpecification.adminAmount"
            :max="donationSettings.max"
            :min="donationSettings.min"
            :display-fee="false"
            :rules="[
              rules.requiredField(String(t('donationDetails.form.donationAmount.label'))),
              validateDonationAmount,
            ]"
            data-cy="paymentdelivery-view-donation-amount-component"
            density="comfortable"
          />
        </v-card>
      </v-form>

      <!-- Anchor tag  for seeing tax information-->
      <div class="mb-8">
        <a
          href="#"
          class="primary--text tax-anchor"
          @click.prevent="showTaxDialog = true"
        >
          {{ t('donationGuide.payment.taxReductionLinkText') }}
        </a>
      </div>

      <!-- Tax reduction modal component -->
      <TaxReductionModalComponent v-model="showTaxDialog" />


      <!-- Old tax reduction area, not used anymore-->
      <!-- <tax-reduction-information-component /> -->

      <!-- Total and fee area -->
      <div class="donation-summary mt-4 pb-4">
        <div class="donation-summary__row">
          <span class="donation-summary__label">{{
            t('donationGuide.payment.summary.yourDonation')
          }}</span>
          <span class="donation-summary__value">{{
            formatCurrency(donationDetails.paymentSpecification.causeAmount)
          }}</span>
        </div>
        <div v-if="donationDetails.paymentSpecification.adminAmount > 0" class="donation-summary__row">
          <span class="donation-summary__label">
            {{ t('donationGuide.payment.summary.fee') }}
            <v-tooltip location="top" :open-delay="0" :close-delay="1000" :max-width="300" color="white">
              <template #activator="{ props }">
                <v-icon v-bind="props" size="small" class="ml-1 fee-info-icon">mdi-information</v-icon>
              </template>
              <span>{{ t('donationDetails.form.adminFee.hint') }}</span>
            </v-tooltip>
          </span>
          <span class="donation-summary__value"
            >+ {{ formatCurrency(donationDetails.paymentSpecification.adminAmount) }}</span
          >
        </div>
        <v-divider class="my-6"/>
        <div class="donation-summary__row donation-summary__row--total mt-4">
          <span class="donation-summary__label">{{
            t('donationGuide.payment.summary.total')
          }}</span>
          <span class="donation-summary__value">{{ formatCurrency(totalAmountCalculated) }}</span>
        </div>
      </div>
    </v-card-text>

    <donation-flow-buttons
      previous-id="paymentDeliveryViewBackButton"
      next-id="paymentDeliveryViewNextButton"
      previous-data-cy="paymentdelivery-view-previous-btn"
      next-data-cy="paymentdelivery-view-next-btn"
      @previous-step="$emit('previousStep')"
      @next-step="next"
    />
  </v-card>
  <div v-else class="step-fallback">
    <p>{{ t('payment.loadingFallback') }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import store from '@/store';
import { useLabel } from '@/composables/useLabel';
import { useAdminFee } from '@/composables/useAdminFee';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';

import { useValidationRules } from '@/composables/useValidationRules';
import { RequestSession } from '@/interfaces/requestSession';
import { Donation } from '@/interfaces/donation/donation';
import { Type as PaymentType } from '@/interfaces/economy/paymentMethod';
import { DeliveryMethod, Type } from '@/classes/DeliveryMethod';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import DeliveryMethodSelectComponent from '@/components/DeliveryMethodSelectComponent.vue';
import DonationAmountComponent from '@/components/payment/DonationAmountComponent.vue';
import PaymentMethodSelectComponent from '@/components/payment/PaymentMethodSelectComponent.vue';
import OptionLabel from '@/components/OptionLabel.vue';
import TaxReductionModalComponent from '@/components/taxreduction/TaxReductionModalComponent.vue';

// modal logic uses translation values directly, no import needed
import { scrollToFirstInvalid } from '@/utils/formHelpers';
import { Intermediary } from '@/interfaces/intermediary/intermediary';
import { isMemoryGift as isMemoryGiftFunc } from '@/classes/DonationType';

import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { getDonationSettings } from '@/utils/donationSettings';
import { getCurrencyForLocation } from '@/utils/currency';

import { guideErrorMessage, guideHttpStatus } from '@/api/mainApi';

const { t } = useLabel();
const { calculateAdminFee } = useAdminFee();

const showTaxDialog = ref(false);

defineProps({
  value: {
    type: Object,
    default: null,
  },
  showPrev: {
    type: Boolean,
    default: true,
  },
  compact: Boolean,
});

const emit = defineEmits(['previousStep', 'nextStep']);

const getDonationStepFlowVersion = computed(
  () => store.state.donationGuideVersion || DonationGuideVersion.V1
);

watch(
  () => store.state.donationGuideVersion,
  (val) => {
    try {
      paymentDeliveryStep.value = steps.value.PAYMENT_AND_DELIVERY;
    } catch (err) {
      window.log?.error('[Guide] paymentDelivery.versionChange — Failed to update payment step after guide version change', {
        message: guideErrorMessage(err),
        donationGuideVersion: val,
        ...paymentDeliveryCtx(),
      });
      store.commit('setSnackbar', {
        active: true,
        color: 'error',
        text: 'An unexpected error occurred while updating the payment step',
      });
    }
  }
);

const { rules: baseRules } = useValidationRules(t);

const rules = computed(() => ({
  // use base rules from composable where appropriate
  requiredField: (fieldName: string) => baseRules.value.requiredField(fieldName),
  email: baseRules.value.email,
  mobilePhoneNumber: baseRules.value.mobilePhoneNumber,
  numeric: (v) => (!isNaN(parseFloat(v)) && isFinite(v)) || t('validation.numeric'),
  minValue: (min: number) => (v) => parseInt(v) >= min || t('validation.minValue', { min }),
  maxValue: (max: number) => (v) => parseInt(v) <= max || t('validation.maxValue', { max }),
  length: (length: number) => (v) =>
    (v && v.length <= length) || t('validation.length', { length }),
  url: (v) =>
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/.test(
      v
    ) || t('validation.url'),
  required: baseRules.value.required,
}));

const valid = ref(true);
const deliveryMethodsRef = ref<Array<any>>([]);
const invoiceEmail = ref('');
const donationDetails = ref({
  deliveryMethod: undefined,
  paymentSpecification: {
    paymentMethod: undefined,
    adminAmount: 0,
    causeAmount: null,
    adminPercentage: null,
    // currencyCode will be filled on commit
  },
  recipient: {
    email: '',
    name: '',
    address: {
      address1: '',
      address2: '',
      address3: '',
      city: '',
      country: '',
      postcode: '',
    },
    isFuneralHome: false,
  },
});
const mobileNumber = ref('');
const paymentDeliveryStep = ref<number>(stepV1.PAYMENT_AND_DELIVERY);
const adminPercentage = ref<number | null>(null);

const form = ref();

const donationSettings = computed(() => getDonationSettings(store.state.userLocation ?? 'SWEDEN'));

const steps = computed(() =>
  getDonationStepFlowVersion.value === DonationGuideVersion.V1 ? stepV1 : stepV2
);

// formatting helpers & calculated totals
const formatCurrency = (amount: number | null) => {
  if (amount === null || amount === undefined) return '';
  const currency = getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const totalAmountCalculated = computed(() => {
  const raw = donationDetails.value.paymentSpecification.causeAmount;
  const donationAmount = typeof raw === 'number' ? raw : raw ? parseInt(String(raw), 10) : NaN;
  if (Number.isNaN(donationAmount)) return null;
  if (
    donationSettings.value.min <= donationAmount &&
    donationAmount <= donationSettings.value.max
  ) {
    return donationAmount + donationDetails.value.paymentSpecification.adminAmount;
  }
  return null;
});

const hasSelectedAmount = computed(() => {
  return totalAmountCalculated.value !== null && totalAmountCalculated.value !== undefined;
});

const isEmailInvoice = computed(
  () =>
    donationDetails.value.paymentSpecification.paymentMethod &&
    donationDetails.value.paymentSpecification.paymentMethod.type === PaymentType.INVOICE_EMAIL
);

// Helper `isInvoicePayment` removed from here (not used in this view) to avoid unused-symbol lint warnings.

const isPhoneBased = computed(() => {
  return (
    (!!donationDetails.value.paymentSpecification &&
      !!donationDetails.value.paymentSpecification.paymentMethod &&
      donationDetails.value.paymentSpecification.paymentMethod.type === PaymentType.SWISH) ||
    (!!donationDetails.value.paymentSpecification &&
      !!donationDetails.value.paymentSpecification.paymentMethod &&
      donationDetails.value.paymentSpecification.paymentMethod.type === PaymentType.PAYLINK_SMS)
  );
});

const deliveryIsEmail = computed(
  () =>
    donationDetails.value.deliveryMethod && donationDetails.value.deliveryMethod.type === Type.EMAIL
);

const isMemoryGift = computed(() => {
  const isMemory = donation.value && isMemoryGiftFunc(donation.value.donationType.type);
  return isMemory;
});

const requestSession = computed((): RequestSession => store.state.requestSession!);

const donation = computed((): Donation => store.state.donation!);

function paymentDeliveryCtx() {
  return {
    donationId: donation.value?.id,
    serialnumber: donation.value?.serialnumber,
    organizationGroupId: requestSession.value?.organizationGroup?.id,
  };
}

const currentStep = computed(() => {
  const step = store.state.currentStep;
  return step;
});

const deliveryMethods = computed(() => {
  const methods = requestSession.value?.deliveryMethods || [];
  return methods;
});

// Watcher to update deliveryMethodsRef when requestSession changes
watch(
  () => requestSession.value?.deliveryMethods,
  (newMethods) => {
    try {
      if (Array.isArray(newMethods) && newMethods.length > 0) {
        deliveryMethodsRef.value = newMethods;
        // Try to preserve any previously selected delivery in the main donation
        try {
          if (
            !donationDetails.value.deliveryMethod &&
            donation.value &&
            donation.value.deliveryMethod
          ) {
            const match = newMethods.find(
              (m) =>
                m.id === donation.value.deliveryMethod.id ||
                m.type === donation.value.deliveryMethod.type
            );
            if (match) {
              donationDetails.value.deliveryMethod = match;
            }
          }
        } catch {
          store.commit('setSnackbar', {
            active: true,
            color: 'error',
            text: 'Failed to synchronize delivery methods',
          });
        }

        // If still no selection, run default logic to pick a sensible option
        if (!donationDetails.value.deliveryMethod) {
          if (typeof setDefaultDeliveryMethod === 'function') {
            setDefaultDeliveryMethod();
          } else {
            nextTick(() => {
              try {
                setDefaultDeliveryMethod();
              } catch {
                store.commit('setSnackbar', {
                  active: true,
                  color: 'error',
                  text: 'Failed to set default delivery method',
                });
              }
            });
          }
        }
      }
    } catch (err) {
      window.log?.error('[Guide] paymentDelivery.deliveryMethodsWatcher — Unexpected error handling delivery methods', {
        message: guideErrorMessage(err),
        ...paymentDeliveryCtx(),
      });
      store.commit('setSnackbar', {
        active: true,
        color: 'error',
        text: 'An unexpected error occurred while handling delivery methods',
      });
    }
  },
  { deep: true, immediate: true }
);

// Also watch the entire requestSession to catch any changes
watch(
  requestSession,
  (newSession) => {
    try {
      if (Array.isArray(newSession?.deliveryMethods) && newSession.deliveryMethods.length > 0) {
        deliveryMethodsRef.value = newSession.deliveryMethods;

        // Try to preserve any previously selected delivery in the main donation
        try {
          if (
            !donationDetails.value.deliveryMethod &&
            donation.value &&
            donation.value.deliveryMethod
          ) {
            const match = newSession.deliveryMethods.find(
              (m) =>
                m.id === donation.value.deliveryMethod.id ||
                m.type === donation.value.deliveryMethod.type
            );
            if (match) {
              donationDetails.value.deliveryMethod = match;
            }
          }
        } catch {
          store.commit('setSnackbar', {
            active: true,
            color: 'error',
            text: 'Failed to synchronize request session',
          });
        }

        // If still no selection, run default logic to pick a sensible option
        if (!donationDetails.value.deliveryMethod) {
          if (typeof setDefaultDeliveryMethod === 'function') {
            setDefaultDeliveryMethod();
          } else {
            nextTick(() => {
              try {
                setDefaultDeliveryMethod();
              } catch {
                store.commit('setSnackbar', {
                  active: true,
                  color: 'error',
                  text: 'Failed to set default delivery method',
                });
              }
            });
          }
        }
      }
    } catch (err) {
      window.log?.error('[Guide] paymentDelivery.requestSessionWatcher — Unexpected error handling request session', {
        message: guideErrorMessage(err),
        ...paymentDeliveryCtx(),
      });
      store.commit('setSnackbar', {
        active: true,
        color: 'error',
        text: 'An unexpected error occurred while handling request session',
      });
    }
  },
  { deep: true }
);
// Auto select first item in payment method array.
watch(
  () => requestSession.value?.paymentMethods,
  (methods) => {
      donationDetails.value.paymentSpecification.paymentMethod = methods[0];
  },
  { immediate: true, deep: true }
);

const mobileLabel = computed((): string => {
  if (
    donationDetails.value.paymentSpecification &&
    donationDetails.value.paymentSpecification.paymentMethod &&
    donationDetails.value.paymentSpecification.paymentMethod.type === PaymentType.PAYLINK_SMS
  ) {
    return String(t('payment.customerMobileForPayLink'));
  } else {
    if (
      requestSession.value &&
      (requestSession.value.incomingChannel === 'Bitnet' ||
        requestSession.value.incomingChannel === 'Bitnet - förmedling')
    ) {
      return String(t('payment.customerMobileForSwish'));
    } else {
      return String(t('payment.yourMobileForSwish'));
    }
  }
});

const caseRecipientHasDefaultDelivery = computed(
  () =>
    donation.value.donationCase != null &&
    donation.value.donationCase.recipient != null &&
    donation.value.donationCase.recipient.deliveryMethod != null
);

const caseRecipientNoCertificateHandling = computed(
  () =>
    donation.value.donationCase != null &&
    donation.value.donationCase.recipient != null &&
    donation.value.donationCase.recipient.noCertificateHandling
);

function deliveryMethodChanged() {
  // Use local, safe checks instead of referencing computed refs directly to avoid TDZ errors during setup
  const hasCaseRecipient = !!(
    donation.value &&
    donation.value.donationCase &&
    donation.value.donationCase.recipient &&
    donation.value.donationCase.recipient.deliveryMethod
  );
  const email = hasCaseRecipient ? donation.value.donationCase.recipient.email : '';
  const name = hasCaseRecipient ? donation.value.donationCase.recipient.name : '';
  const deliveryIsEmailLocal = !!(
    donationDetails.value &&
    donationDetails.value.deliveryMethod &&
    donationDetails.value.deliveryMethod.type === Type.EMAIL
  );

  if (hasCaseRecipient && !deliveryIsEmailLocal) {
    donationDetails.value.recipient = donation.value.donationCase.recipient;
  } else if (deliveryIsEmailLocal) {
    donationDetails.value.recipient = {
      email: email,
      name: name,
      address: {
        address1: '',
        address2: '',
        address3: '',
        city: '',
        country: '',
        postcode: '',
      },
      isFuneralHome: false,
    };
  }
  // then get correct adminFee
  getAdminFee();
}

async function getAdminFee() {
  try {
    if (
      donationDetails.value &&
      donationDetails.value.paymentSpecification.paymentMethod
    ) {
      const intermediary: Intermediary | null = requestSession.value.intermediary;
      // ensure required donation fields exist
      if (!donation.value?.donationCause || !donation.value?.donationType) return;
      const currency = getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');
      const data = await calculateAdminFee(
        donation.value.donationCause.id,
        donation.value.donationType.id,
        {
          deliveryMethodId: isMemoryGift.value ? donationDetails.value.deliveryMethod?.id : null,
          paymentMethodId: donationDetails.value.paymentSpecification.paymentMethod.id,
          intermediaryId: intermediary ? intermediary.id : null,
          currency: currency,
        }
      );
      if (data) {
        if (!!data.adminAmount) {
          donationDetails.value.paymentSpecification.adminAmount = data.adminAmount;
          adminPercentage.value = null;
          donationDetails.value.paymentSpecification.adminPercentage = null;
        } else {
          // Default to 0 if percentage is null 
          adminPercentage.value = data.adminPercentage ?? 0;
          donationDetails.value.paymentSpecification.adminAmount =
            (Number(donationDetails.value.paymentSpecification.causeAmount) || 0) *
            (adminPercentage.value ?? 0);
          donationDetails.value.paymentSpecification.adminPercentage = adminPercentage.value;
        }
      }
    }
  } catch (err: any) {
    const is404 = err?.response?.status === 404 || err?.status === 404;
    if (is404 || !isMemoryGift.value) {
      // No admin fee configured for this combination, or this is a contribution flow.
      // Use 0 not null: backend seems to have issues if adminPercentage is null and it can't find an AdminFee record.
      adminPercentage.value = 0;
      donationDetails.value.paymentSpecification.adminAmount = 0;
      donationDetails.value.paymentSpecification.adminPercentage = 0;
      window.log?.debug?.('PaymentDeliveryView getAdminFee: no admin fee found, defaulting to 0', {
        causeId: donation.value?.donationCause?.id,
      });
    } else {
      window.log?.error('[Guide] paymentDelivery.getAdminFee — Failed to calculate admin fee', {
        message: guideErrorMessage(err),
        httpStatus: guideHttpStatus(err),
        ...paymentDeliveryCtx(),
      });
      store.commit('setSnackbar', {
        active: true,
        color: 'error',
        text: 'Failed to calculate admin fee',
      });
    }
  }
}

const validateDonationAmount = (value) => {
  const donationAmount = parseInt(value);
  const trueDonationAmount = parseFloat(value);
  if (typeof donationAmount != 'number') {
    return t('validation.numeric');
  } else if (!Number.isInteger(trueDonationAmount)) {
    return t('validation.nonInteger');
  } else if (/^\d+\.\d+$/.test(value)) {
    // if the value is an integer, but of a bad form (e.g. 10.0)
    return t('validation.hasDecimal');
  } else if (donationSettings.value.max < donationAmount) {
    return t('validation.maxValue', { max: donationSettings.value.max });
  } else if (donationAmount < donationSettings.value.min) {
    return t('validation.minValue', { min: donationSettings.value.min });
  }

  return true;
};

const next = async () => {
  try {
    // Manual validation for required fields
    let isValid = true;
    let firstInvalidField: string | null = null;

    // Delivery method is only required for memory gifts (contributions do not have one)
    if (isMemoryGift.value && !donationDetails.value.deliveryMethod) {
      isValid = false;
      firstInvalidField = 'delivery-method-select';
    }

    // Payment method is always required
    if (!donationDetails.value.paymentSpecification?.paymentMethod) {
      isValid = false;
      if (!firstInvalidField) firstInvalidField = 'payment-method-select';
    }

    // If delivery is email, recipient details are required
    if (donationDetails.value.deliveryMethod?.type === Type.EMAIL) {
      if (!donationDetails.value.recipient?.name || !donationDetails.value.recipient.name.trim()) {
        isValid = false;
        if (!firstInvalidField) firstInvalidField = 'namefield';
      }
      if (
        !donationDetails.value.recipient?.email ||
        !donationDetails.value.recipient.email.trim()
      ) {
        isValid = false;
        if (!firstInvalidField) firstInvalidField = 'emailfield';
      }
    }

    // If payment method is invoice email, invoice email is required
    if (isEmailInvoice.value) {
      if (!invoiceEmail.value || !invoiceEmail.value.trim()) {
        isValid = false;
        if (!firstInvalidField) firstInvalidField = 'invoice-email';
      }
    }

    // If payment method requires phone, mobile number is required
    if (isPhoneBased.value) {
      if (!mobileNumber.value || !mobileNumber.value.trim()) {
        isValid = false;
        if (!firstInvalidField) firstInvalidField = 'mobilenumber';
      }
    }

    // Ensure an amount is selected/entered and meets the minimum and maximum
    const amount = donationDetails.value.paymentSpecification?.causeAmount;
    if (!amount || Number(amount) <= 0 || Number(amount) < donationSettings.value.min || Number(amount) > donationSettings.value.max) {
      isValid = false;
      if (!firstInvalidField) firstInvalidField = 'paymentdelivery-view-donation-amount-component';
    }

    if (!isValid) {
      try {
        form.value.validate();
      } catch {
        /* ignore */
      }
      return;
    }

    try {
      const validForm = Boolean(await form.value.validate());
      if (!validForm) {
        try {
          scrollToFirstInvalid(form.value, null);
        } catch {
          /* no-op */
        }
        return;
      }
    } catch (err) {
      window.log?.error('[Guide] paymentDelivery.validate — Payment and delivery form validation failed unexpectedly', {
        message: guideErrorMessage(err),
        ...paymentDeliveryCtx(),
      });
      try {
        scrollToFirstInvalid(form.value, null);
      } catch {
        /* no-op */
      }
      try {
        store.commit('setSnackbar', {
          active: true,
          text: 'Validation failed unexpectedly. Please try again.',
          color: 'error',
        } as any);
      } catch {}
      return;
    }

    const donationObj: Donation = { ...donation.value };
    // Delivery method only applies to memory gifts (certificate delivery).
    // Contributions do not have a certificate so deliveryMethod must be null
    donationObj.deliveryMethod = isMemoryGift.value ? donationDetails.value.deliveryMethod : null;
    donationObj.paymentSpecification = donationDetails.value.paymentSpecification as any;

    // PaymentMethodSelectComponent adds a `title` field for Vuetify v-select display (return-object).
    // Strip it before committing — the backend doesn't expect it and it can trigger unexpected behaviour.
    if (donationObj.paymentSpecification?.paymentMethod) {
      const { title: _t, ...paymentMethodWithoutTitle } = donationDetails.value.paymentSpecification.paymentMethod as any;
      donationObj.paymentSpecification.paymentMethod = paymentMethodWithoutTitle;
    }

    // Ensure a currency code is recorded on the paymentSpecification so downstream components
    // and formatting can display the correct currency
    try {
      const ps: any = donationObj.paymentSpecification || {};
      ps.currencyCode = String(
        getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN')
      ).toUpperCase();
      donationObj.paymentSpecification = ps;
    } catch (err) {
      window.log?.error('[Guide] paymentDelivery.currencyCode — Could not determine currency for payment', {
        message: guideErrorMessage(err),
        userLocation: store.state.userLocation,
        ...paymentDeliveryCtx(),
      });
      store.commit('setSnackbar', {
        active: true,
        color: 'error',
        text: 'Could not determine currency for payment',
      });
      (donationObj.paymentSpecification as any).currencyCode = 'SEK';
    }

    if (donationObj.deliveryMethod?.type === Type.EMAIL) {
      donationObj.recipient = donationDetails.value.recipient as any;
    } else if (donation.value.donationCase?.recipient) {
      donationObj.recipient = donation.value.donationCase.recipient;
    } else {
      donationObj.recipient = null;
    }


    store.commit('updateDonation', donationObj);
    emit('nextStep');
  } catch (err) {
    window.log?.error('[Guide] paymentDelivery.next — Unhandled error in payment and delivery step', {
      message: guideErrorMessage(err),
      ...paymentDeliveryCtx(),
    });
    try {
      store.commit('setSnackbar', {
        active: true,
        text: 'An unexpected error occurred while proceeding to payment.',
        color: 'error',
      } as any);
    } catch {
      // no-op
    }
  }
};

const handlePostOrExternalAgentDeliveryMethod = () => {
  const filteredMethods: Array<DeliveryMethod> = requestSession.value.deliveryMethods.slice();
  if (
    isMemoryGift.value &&
    (!requestSession.value.intermediary ||
      (requestSession.value.donationCases && requestSession.value.donationCases.length > 0))
  ) {
    // if there is a recipient set for the case and the recipient has a deliveryMethod with type EXTERNAL_AGENT
    if (
      caseRecipientHasDefaultDelivery.value &&
      (donation.value.donationCase.recipient.deliveryMethod.type === Type.EXTERNAL_AGENT ||
        donation.value.donationCase.recipient.deliveryMethod.type === Type.PRINT)
    ) {
      // First we remove type POST and EMAIL if it exists in the list
      const postIndex = filteredMethods.findIndex((method) => method.type === Type.POST);
      if (postIndex >= 0) {
        filteredMethods.splice(postIndex, 1);
      }
      const emailIndex = filteredMethods.findIndex((method) => method.type === Type.EMAIL);
      if (emailIndex >= 0) {
        filteredMethods.splice(emailIndex, 1);
      }
      // Then we add type EXTERNAL_AGENT if it doesn't exist
      const externalIndex = filteredMethods.findIndex(
        (method) => method.type === Type.EXTERNAL_AGENT
      );
      if (externalIndex < 0) {
        filteredMethods.push({
          id: 4,
          type: Type.EXTERNAL_AGENT,
          name: t('payment.deliveryMethod.externalAgent.name'),
          description: t('payment.deliveryMethod.externalAgent.description'),
          nameTranslationKey: 'deliveryMethod.external.name',
          descriptionTranslationKey: 'deliveryMethod.external.description',
          color: 'secondary',
          icon: 'mdi-hand-heart',
        } as DeliveryMethod);
      }
      // lastly we pre-select first item in the list
      donationDetails.value.deliveryMethod = filteredMethods[0];
    } else if (filteredMethods.length > 1) {
      // otherwise we remove external agent type deliverymethod if it exists and there is another deliverymethod to choose from
      const index = filteredMethods.findIndex((method) => method.type === Type.EXTERNAL_AGENT);
      if (index >= 0) {
        filteredMethods.splice(index, 1);
      }
    }
  }
  // deliveryMethods assignment removed - now using computed property from requestSession
};

const handleNoCertificateHandling = () => {
  let filteredMethods: Array<DeliveryMethod> = requestSession.value.deliveryMethods.slice();
  if (
    isMemoryGift.value &&
    requestSession.value.donationCases &&
    requestSession.value.donationCases.length > 0
  ) {
    // if there is a recipient set for the case and the recipient has a deliveryMethod with type EXTERNAL_AGENT
    if (caseRecipientNoCertificateHandling.value) {
      // First we remove everything except Email
      filteredMethods = filteredMethods.filter((fm) => fm.type === Type.EMAIL);
      // Then we add type Email if it doesn't exist
      const index = filteredMethods.findIndex((method) => method.type === Type.EMAIL);
      if (index < 0) {
        filteredMethods.push({
          id: 1,
          type: Type.EMAIL,
          name: t('payment.deliveryMethod.email.name'),
          description: t('payment.deliveryMethod.email.description'),
          color: 'primary',
          icon: 'mdi-email-outline',
        } as DeliveryMethod);
      }
    }
  }
};

function setDefaultDeliveryMethod() {
  // Contributions do not have a delivery method — only memory gifts need one
  if (!isMemoryGift.value) return;
  if (!Array.isArray(deliveryMethods.value) || deliveryMethods.value.length === 0) return;

  // Reset if currently selected method is not available
  if (donationDetails.value.deliveryMethod) {
    const exists = deliveryMethods.value.some(
      (m) => m.type === donationDetails.value.deliveryMethod!.type
    );
    if (!exists) donationDetails.value.deliveryMethod = null;
  }

  // If still no selection, try case recipient's preferred method (for memory gifts)
  if (!donationDetails.value.deliveryMethod) {
    if (isMemoryGift.value && caseRecipientHasDefaultDelivery.value) {
      const type = donation.value?.donationCase?.recipient?.deliveryMethod?.type;
      const idx = deliveryMethods.value.findIndex((m) => m.type === type);
      if (idx >= 0 && deliveryMethodsRef.value.length > idx) {
        donationDetails.value.deliveryMethod = deliveryMethodsRef.value[idx];
      }
    }

    // As a fallback, pick the first available
    if (!donationDetails.value.deliveryMethod && deliveryMethodsRef.value.length > 0) {
      donationDetails.value.deliveryMethod = deliveryMethodsRef.value[0];
    }
  }

  // If certificate handling disabled, force email if available
  if (
    isMemoryGift.value &&
    requestSession.value?.donationCases?.length > 0 &&
    caseRecipientNoCertificateHandling.value &&
    deliveryMethodsRef.value.length > 0
  ) {
    donationDetails.value.deliveryMethod = deliveryMethodsRef.value[0];
  }

  // No-op return
}
// Only auto-select a delivery method for memory gifts; contributions never have one
if (isMemoryGift.value && !donationDetails.value.deliveryMethod && deliveryMethodsRef.value.length > 0) {
  donationDetails.value.deliveryMethod = deliveryMethodsRef.value[0];

  deliveryMethodChanged();
}

// Debug: watch for unexpected changes to delivery selection
watch(
  () => donationDetails.value.deliveryMethod,
  () => {},
  { deep: true }
);

watch(isEmailInvoice, (val) => {
  if (
    val &&
    (!donation.value.donor.invoiceEmail || donation.value.donor.invoiceEmail.length === 0)
  ) {
    invoiceEmail.value = donation.value.donor.email;
  } else {
    invoiceEmail.value = '';
  }
});

watch(invoiceEmail, (val) => {
  donation.value.donor.invoiceEmail = val;
});

watch(mobileNumber, (val) => {
  donation.value.donor.mobileNumber = val;
});

watch(currentStep, async (val) => {
  try {
    if (val == steps.value.PAYMENT_AND_DELIVERY) {
      // Ensure local edit model reflects current donation (user might have selected delivery earlier)
      try {
        if (donation.value && donation.value.deliveryMethod) {
          donationDetails.value.deliveryMethod = donation.value.deliveryMethod;
        }
        if (donation.value && donation.value.paymentSpecification) {
          // clone primitive fields to avoid accidental shared refs and preserve explicit zero values
          donationDetails.value.paymentSpecification.paymentMethod =
            donation.value.paymentSpecification.paymentMethod ??
            donationDetails.value.paymentSpecification.paymentMethod;
          donationDetails.value.paymentSpecification.causeAmount =
            typeof donation.value.paymentSpecification.causeAmount === 'number'
              ? donation.value.paymentSpecification.causeAmount
              : donationDetails.value.paymentSpecification.causeAmount;
          donationDetails.value.paymentSpecification.adminAmount =
            typeof donation.value.paymentSpecification.adminAmount === 'number'
              ? donation.value.paymentSpecification.adminAmount
              : donationDetails.value.paymentSpecification.adminAmount;
        }
        if (donation.value && donation.value.recipient) {
          donationDetails.value.recipient = { ...donation.value.recipient };
        }
      } catch {
        store.commit('setSnackbar', {
          active: true,
          color: 'error',
          text: 'Failed to sync donation details',
        });
      }

      // The order in which these are called is very important! DON'T CHANGE
      handlePostOrExternalAgentDeliveryMethod();
      handleNoCertificateHandling();

      await getAdminFee();
    }
  } catch (err) {
    window.log?.error('[Guide] paymentDelivery.currentStep — Error entering payment and delivery step', {
      message: guideErrorMessage(err),
      step: val,
      ...paymentDeliveryCtx(),
    });
  }
});

watch(
  () => donationDetails.value.paymentSpecification.causeAmount,
  (after: number, before: number) => {
    try {
      const afterNum = Number(after || 0);
      if (
        !!adminPercentage.value &&
        afterNum !== Number(before || 0) &&
        afterNum >= donationSettings.value.min &&
        afterNum <= donationSettings.value.max
      ) {
        donationDetails.value.paymentSpecification.adminAmount = afterNum * adminPercentage.value;
      }
    } catch {
      store.commit('setSnackbar', {
        active: true,
        color: 'error',
        text: 'An unexpected error occurred while updating donation amount',
      });
    }
  }
);

onMounted(() => {
  paymentDeliveryStep.value = steps.value.PAYMENT_AND_DELIVERY;
});
</script>
<style scoped>
.no-sender-name {
  margin: 0;
  padding: 0;
}
.tax-anchor {
  text-decoration: underline !important; 
  cursor: pointer !important;
}
.donation-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  margin-top: 5px;
}
.donation-summary__label {
  font-weight: 700;
  font-family: "Lato", sans-serif;
  color: #1F2A37;
}
.donation-summary__value {
  font-weight: 400;
  font-family: "Lato", sans-serif;
  color: #1F2A37;
  line-height: 155%;
}
</style>
